import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  LinearProgress
} from '@mui/material';
import {
  PhotoCamera,
  Close,
  Crop,
  CloudUpload
} from '@mui/icons-material';
import { resizeImage, createThumbnail } from '../services/storageService';
import UserAvatar from './UserAvatar';

interface ImageUploadProps {
  currentImageUrl?: string | null;
  userName: string;
  onImageSelect: (file: File, preview: string) => void;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  userName,
  onImageSelect,
  disabled = false
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // 파일 처리 공통 로직
  const processFile = (file: File) => {
    // 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 검증 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('파일 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    setError(null);
    setSelectedFile(file);

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
      setPreviewOpen(true);
    };
    reader.readAsDataURL(file);
  };

  // 파일 선택 핸들러
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  // 드래그 오버 핸들러
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  // 드래그 리브 핸들러
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  // 드롭 핸들러
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  // 이미지 처리 및 업로드
  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    try {
      setProcessing(true);
      
      // 이미지 리사이즈 (최대 800x600, 품질 0.8)
      const resizedFile = await resizeImage(selectedFile, 800, 600, 0.8);
      
      // 썸네일 생성 (미리보기용)
      const thumbnailUrl = await createThumbnail(resizedFile, 150);
      
      onImageSelect(resizedFile, thumbnailUrl);
      setPreviewOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error: any) {
      setError('이미지 처리 중 오류가 발생했습니다.');
      console.error('Image processing error:', error);
    } finally {
      setProcessing(false);
    }
  };

  // 미리보기 다이얼로그 닫기
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 파일 선택 버튼 클릭
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box>
      {/* 드래그앤드롭 영역 */}
      <Box
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          border: isDragOver ? '2px dashed #1976d2' : '2px dashed transparent',
          borderRadius: 2,
          backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'all 0.2s ease',
          p: 2,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        onClick={!disabled ? handleButtonClick : undefined}
      >
        {/* 현재 프로필 사진과 업로드 버튼 */}
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <UserAvatar
            photoUrl={currentImageUrl}
            name={userName}
            size={120}
          />
          
          <input
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            type="file"
            onChange={handleFileSelect}
            disabled={disabled}
          />
          
          <Button
            variant="outlined"
            component="span"
            startIcon={<PhotoCamera />}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick();
            }}
            disabled={disabled}
            size="small"
          >
            사진 선택
          </Button>

          <Typography 
            variant="caption" 
            color="textSecondary" 
            textAlign="center"
            sx={{ mt: 1 }}
          >
            또는 이미지를 여기로 드래그하세요
          </Typography>

          {error && (
            <Typography color="error" variant="caption" textAlign="center">
              {error}
            </Typography>
          )}
        </Box>

        {/* 드래그 오버 시 오버레이 */}
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderRadius: 2,
              pointerEvents: 'none'
            }}
          >
            <Box textAlign="center">
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" color="primary">
                이미지를 놓아주세요
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* 이미지 미리보기 다이얼로그 */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="between" alignItems="center">
            <Typography variant="h6">프로필 사진 미리보기</Typography>
            <IconButton onClick={handleClosePreview} disabled={processing}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {processing && (
            <Box mb={2}>
              <Typography variant="body2" gutterBottom>
                이미지를 최적화하는 중...
              </Typography>
              <LinearProgress />
            </Box>
          )}

          {previewUrl && (
            <Box display="flex" justifyContent="center" mb={2}>
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            </Box>
          )}

          <Box>
            <Typography variant="body2" color="textSecondary" paragraph>
              • 이미지는 자동으로 최적화됩니다
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              • 권장 크기: 정사각형 비율
            </Typography>
            <Typography variant="body2" color="textSecondary">
              • 지원 형식: JPEG, PNG, WebP
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePreview} disabled={processing}>
            취소
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            disabled={processing || !selectedFile}
            startIcon={processing ? <CloudUpload /> : <Crop />}
          >
            {processing ? '처리 중...' : '적용'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;
